import type { OrderDetails } from '../services/orders.service';

export interface RailBillingEligibility {
  /** Show the enabled "Send Rail Billing" button (qualifies + required + not yet
   * billed/sent + not terminated/template). */
  canSend: boolean;
  /** Show the disabled "Railbill Not Req." button — qualifies for rail billing
   * but the SCAC doesn't require it. */
  showNotRequired: boolean;
  /** Show the "Cancel Rail Billing" button (already sent, move not terminated). */
  canCancel: boolean;
  /** Default EMPTY/LOADED value for the Send dropdown — EMPTY for import/domestic,
   * LOADED for export. */
  defaultLoadedEmpty: 'EMPTY' | 'LOADED';
}

const EMPTY: RailBillingEligibility = {
  canSend: false,
  showNotRequired: false,
  canCancel: false,
  defaultLoadedEmpty: 'EMPTY',
};

/**
 * Mirrors the v1 lib_editrecord.pl `$inlinerailbillbutton` block (~line 877).
 * The rail-applicable-location + move-type qualification is computed on the
 * backend (`railBillingQualifies`) since it needs location-level flags; this
 * util layers the remaining display gates on top.
 *
 * v1 list/bulk-view guards (isGWITrack / $global_notgwi / $updateall) don't
 * apply on the /pro edit page and are intentionally dropped. The `deleted='N'`
 * guard is enforced server-side (the order wouldn't load otherwise).
 */
export function computeRailBillingEligibility(
  od: OrderDetails | null | undefined,
): RailBillingEligibility {
  if (!od) return EMPTY;

  const isTemplate = od.template === 'Y';
  const isTerminated = Boolean((od.traceTerminateDatetime || '').trim());
  const qualifies = Boolean(od.railBillingQualifies);
  const railbilled = od.railbilled === 'Y';
  const sent = od.railbillSent === 'Y';
  const required = od.railbillingReqd === 'Y';

  const defaultLoadedEmpty: 'EMPTY' | 'LOADED' =
    od.moveType === 'IMPORT' || od.moveType === 'DOMESTIC' ? 'EMPTY' : 'LOADED';

  if (isTemplate || isTerminated) {
    return { ...EMPTY, defaultLoadedEmpty };
  }

  // Not yet billed or sent → Send (when required) or "Not Req." (when not).
  const canSend = qualifies && !railbilled && !sent && required;
  const showNotRequired = qualifies && !railbilled && !sent && !required;
  // Already sent (and not terminated) → Cancel.
  const canCancel = sent;

  return { canSend, showNotRequired, canCancel, defaultLoadedEmpty };
}

export interface RailBillingValidation {
  ok: boolean;
  /** Human-readable list of missing required fields, for the alert message. */
  missing: string[];
}

/**
 * Ports the v1 `railBillingCheck()` JS function — the field requirements that
 * must be satisfied before a rail bill can be sent. IMPORT needs an out-gate
 * date; EXPORT needs piece count, seal, a driver on the pickup (originate) leg,
 * and a weight in lbs/kg.
 */
export function railBillingCheck(od: OrderDetails): RailBillingValidation {
  const missing: string[] = [];

  if (od.moveType === 'IMPORT') {
    if (!(od.outGateDatetime || '').trim()) missing.push('Out Gate Date/Time');
  } else if (od.moveType === 'EXPORT') {
    if (!(od.pieceCount || '').trim()) missing.push('Piece count');
    if (!(od.sealNumber || '').trim()) missing.push('Seal Number');

    const pickupLeg = od.legs?.find((l) => l.legnum === od.originateLeg);
    if (!(pickupLeg?.driver || '').trim()) missing.push('Pickup driver leg');

    if (!od.kgs || !od.lbs) missing.push('The Weight in lbs/kg');
  }

  return { ok: missing.length === 0, missing };
}
